import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';

const StyleCreditsContext = createContext();

export const useStyleCredits = () => {
  const context = useContext(StyleCreditsContext);
  if (!context) {
    throw new Error('useStyleCredits must be used within a StyleCreditsProvider');
  }
  return context;
};

export const StyleCreditsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [credits, setCredits] = useState({
    totalCredits: 0,
    usedCredits: 0,
    availableCredits: 0,
    creditsHistory: [],
    loading: false,
    error: null
  });

  // Credit earning percentage (10% of order value)
  const CREDITS_PERCENTAGE = 0.10;
  
  // Credit expiry in months
  const CREDITS_EXPIRY_MONTHS = 6;

  // Load user's credits from Firestore (stored in users collection)
  const loadUserCredits = useCallback(async (forceRefresh = false) => {
    if (!currentUser?.uid) {
      setCredits(prev => ({ ...prev, totalCredits: 0, usedCredits: 0, availableCredits: 0, creditsHistory: [] }));
      return;
    }

    try {
      setCredits(prev => ({ ...prev, loading: true, error: null }));
      
      // Use standard Firebase getDoc - no caching
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const creditsData = userData.styleCredits || {
          totalCredits: 0,
          usedCredits: 0,
          creditsHistory: []
        };
        
        // Check for NaN values and fix them automatically
        if (isNaN(creditsData.usedCredits)) {
          try {
            const userRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userRef, {
              'styleCredits.usedCredits': 0,
              'styleCredits.lastUpdated': serverTimestamp()
            });
            // Update creditsData for current calculation
            creditsData.usedCredits = 0;
          } catch (migrationError) {
            console.error('🔧 AUTO_MIGRATION: Failed to fix NaN usedCredits:', migrationError);
          }
        }
        
        const totalCredits = Number(creditsData.totalCredits) || 0;
        const usedCredits = Number(creditsData.usedCredits) || 0;
        const availableCredits = Math.max(0, totalCredits - usedCredits);
        
        
        setCredits(prev => ({
          ...prev,
          totalCredits: totalCredits,
          usedCredits: usedCredits,
          availableCredits,
          creditsHistory: creditsData.creditsHistory || [],
          loading: false
        }));
        
      } else {
        // User document doesn't exist, initialize with empty credits
        setCredits(prev => ({
          ...prev,
          totalCredits: 0,
          usedCredits: 0,
          availableCredits: 0,
          creditsHistory: [],
          loading: false
        }));
      }
    } catch (error) {
      console.error('Error loading user credits:', error);
      setCredits(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message 
      }));
    }
  }, [currentUser]);

  // Award credits after successful order
  const awardCredits = async (orderData) => {

    
    if (!currentUser?.uid) {
      console.error('🏆 AWARD_CREDITS: No user logged in, throwing error');
      throw new Error('User must be logged in to earn credits');
    }

    try {
      const rawCalculation = orderData.totalAmount * CREDITS_PERCENTAGE;
      const roundedCalculation = Math.round(rawCalculation);
      const creditsToAward = Math.max(1, roundedCalculation);

      
      if (creditsToAward <= 0) {
        return { success: true, creditsAwarded: 0 };
      }

      const userRef = doc(db, 'users', currentUser.uid);
      
      const userSnap = await getDoc(userRef);
      
      const expiryDate = new Date();
      expiryDate.setMonth(expiryDate.getMonth() + CREDITS_EXPIRY_MONTHS);
      
      const creditEntry = {
        type: 'earned',
        amount: creditsToAward,
        orderId: orderData.orderNumber || orderData.id,
        description: `Earned from Order #${orderData.orderNumber || orderData.id}`,
        createdAt: new Date(), // Use regular Date instead of serverTimestamp for arrays
        expiresAt: expiryDate
      };

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const currentCredits = userData.styleCredits || {
          totalCredits: 0,
          usedCredits: 0,
          creditsHistory: []
        };
        

        // Update existing user document with credits
        // Ensure usedCredits is always a number, handle NaN case
        const currentUsedCredits = Number(currentCredits.usedCredits) || 0;
        
        const updateData = {
          'styleCredits.totalCredits': currentCredits.totalCredits + creditsToAward,
          'styleCredits.usedCredits': currentUsedCredits, // Ensure it's a proper number
          'styleCredits.creditsHistory': [...(currentCredits.creditsHistory || []), creditEntry],
          'styleCredits.lastUpdated': serverTimestamp()
        };

        
        await updateDoc(userRef, updateData);

      } else {
        // Create new user document with credits
        const initialUserData = {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName,
          styleCredits: {
            totalCredits: creditsToAward,
            usedCredits: 0,
            creditsHistory: [creditEntry],
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          },
          createdAt: serverTimestamp()
        };
        

        await setDoc(userRef, initialUserData);

      }

      // Reload credits to update UI
      await loadUserCredits();
      

      return { success: true, creditsAwarded: creditsToAward };
    } catch (error) {
      console.error('🏆 AWARD_CREDITS: Error awarding credits:', error);
      console.error('🏆 AWARD_CREDITS: Error details:', {
        message: error.message,
        stack: error.stack,
        orderData: orderData
      });
      return { success: false, error: error.message };
    }
  };

  // Use credits during checkout
  const useCredits = async (amountToUse) => {
    if (!currentUser?.uid) {
      throw new Error('User must be logged in to use credits');
    }

    if (amountToUse <= 0) {
      return { success: true, creditsUsed: 0 };
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('No credits available');
      }

      const userData = userSnap.data();
      const currentCredits = userData.styleCredits || {
        totalCredits: 0,
        usedCredits: 0,
        creditsHistory: []
      };
      
      const availableCredits = currentCredits.totalCredits - currentCredits.usedCredits;
      
      if (availableCredits < amountToUse) {
        throw new Error(`Insufficient credits. Available: ₹${availableCredits}`);
      }

      const creditEntry = {
        type: 'used',
        amount: amountToUse,
        orderId: `checkout_${Date.now()}`,
        description: `Used for checkout`,
        createdAt: new Date() // Use regular Date instead of serverTimestamp for arrays
      };

      // Ensure usedCredits is always a number, handle NaN case
      const currentUsedCredits = Number(currentCredits.usedCredits) || 0;
      const newUsedCredits = currentUsedCredits + amountToUse;
      

      await updateDoc(userRef, {
        'styleCredits.usedCredits': newUsedCredits,
        'styleCredits.creditsHistory': [...(currentCredits.creditsHistory || []), creditEntry],
        'styleCredits.lastUpdated': serverTimestamp()
      });

      // Invalidate cache to ensure fresh data on next load
      
      // Reload credits to update UI
      await loadUserCredits();
      
      return { success: true, creditsUsed: amountToUse };
    } catch (error) {
      console.error('Error using credits:', error);
      return { success: false, error: error.message };
    }
  };

  // Get credits history
  const getCreditsHistory = useCallback(async () => {
    if (!currentUser?.uid) {
      return [];
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        return userData.styleCredits?.creditsHistory || [];
      }
      
      return [];
    } catch (error) {
      console.error('Error getting credits history:', error);
      return [];
    }
  }, [currentUser]);

  // Check if user has enough credits
  const hasEnoughCredits = (amount) => {
    return credits.availableCredits >= amount;
  };

  // Data migration function to fix NaN values in usedCredits
  const fixCreditsData = async () => {
    if (!currentUser?.uid) {
      return { success: false, error: 'User must be logged in' };
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return { success: false, error: 'User document not found' };
      }

      const userData = userSnap.data();
      const currentCredits = userData.styleCredits || {};
      
      // Check if usedCredits is NaN
      if (isNaN(currentCredits.usedCredits)) {

        
        await updateDoc(userRef, {
          'styleCredits.usedCredits': 0,
          'styleCredits.lastUpdated': serverTimestamp()
        });
        
        
        // Reload credits to update UI
        await loadUserCredits();
        
        return { success: true, message: 'Fixed NaN usedCredits value' };
      }
      
      return { success: true, message: 'No migration needed' };
    } catch (error) {
      console.error('Error during credits data migration:', error);
      return { success: false, error: error.message };
    }
  };

  // Get maximum usable credits (capped at available credits)
  const getMaxUsableCredits = (orderTotal) => {
    return Math.min(credits.availableCredits, orderTotal);
  };

  // Load credits when user changes
  useEffect(() => {
    loadUserCredits();
  }, [loadUserCredits]);

  // Clear credits cache
  const clearCreditsCache = useCallback(() => {
    if (currentUser?.uid) {
      console.log('🗑️ Cleared credits cache');
    }
  }, [currentUser]);

  const value = {
    credits,
    loadUserCredits,
    awardCredits,
    useCredits,
    getCreditsHistory,
    hasEnoughCredits,
    getMaxUsableCredits,
    fixCreditsData,
    clearCreditsCache,
    CREDITS_PERCENTAGE,
    CREDITS_EXPIRY_MONTHS
  };

  return (
    <StyleCreditsContext.Provider value={value}>
      {children}
    </StyleCreditsContext.Provider>
  );
};
